import React from "react"
import { planButtonStyle as buttonStyle } from "./PlanPrimitives"

export default function PlanModuleHero({
  isPatientContext,
  effectivePatientId,
  effectivePatientName,
  currentPatientMeta,
  patientFocusedMetrics,
  summary,
  executiveInsights,
  formatMoney,
  createModeOpen,
  onToggleCreateMode,
  onResetPatientContext,
  onOpenPatient,
  onOpenFinance
}) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
        borderRadius: 20,
        padding: 18,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        display: "grid",
        gap: 16
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 14,
          alignItems: "flex-start",
          flexWrap: "wrap"
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              padding: "6px 10px",
              borderRadius: 999,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: 0.45,
              textTransform: "uppercase",
              marginBottom: 10
            }}
          >
            Gestión de planes
          </div>

          <div
            style={{
              fontSize: 30,
              fontWeight: 900,
              color: "#0f172a",
              letterSpacing: "-0.04em",
              lineHeight: 1.02
            }}
          >
            {isPatientContext
              ? `Planes de ${effectivePatientName || "paciente"}`
              : "Planes de tratamiento"}
          </div>

          <div
            style={{
              color: "#64748b",
              marginTop: 6,
              fontSize: 13,
              lineHeight: 1.4,
              maxWidth: 760
            }}
          >
            {isPatientContext
              ? "Seguimiento operativo del paciente seleccionado, sin cargar la pantalla con analítica de más."
              : "Vista operativa de la clínica para ubicar rápido qué plan requiere seguimiento, agendar o cobro."}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {isPatientContext ? (
            <button
              onClick={onResetPatientContext}
              style={{ ...buttonStyle, background: "#ffffff" }}
            >
              Ver vista general
            </button>
          ) : null}

          <button
            onClick={onToggleCreateMode}
            style={{
              ...buttonStyle,
              background: "#eff6ff",
              color: "#1d4ed8",
              border: "1px solid #bfdbfe",
              boxShadow: createModeOpen
                ? "0 0 0 3px rgba(59,130,246,0.12)"
                : "0 6px 18px rgba(15,23,42,0.04)"
            }}
          >
            {createModeOpen ? "Formulario abierto" : "Nuevo plan manual"}
          </button>
        </div>
      </div>

      {isPatientContext ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
              borderTop: "1px solid #eef2f7",
              paddingTop: 14
            }}
          >
            <div style={{ display: "grid", gap: 4 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 900,
                  color: "#0f172a",
                  letterSpacing: "-0.02em"
                }}
              >
                {effectivePatientName || "Paciente"}
              </div>

              <div style={{ fontSize: 13, color: "#64748b" }}>
                {currentPatientMeta?.phone || currentPatientMeta?.email
                  ? [currentPatientMeta?.phone, currentPatientMeta?.email].filter(Boolean).join(" · ")
                  : "Sin datos de contacto"}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {effectivePatientId ? (
                <button
                  onClick={() => onOpenPatient(effectivePatientId)}
                  style={{ ...buttonStyle, background: "#ffffff" }}
                >
                  Abrir paciente
                </button>
              ) : null}

              {effectivePatientId ? (
                <button
                  onClick={() => onOpenFinance(effectivePatientId, "RECEIVABLES")}
                  style={{
                    ...buttonStyle,
                    background: "#f8fafc"
                  }}
                >
                  Abrir finanzas
                </button>
              ) : null}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12
            }}
          >
            <CompactMetric
              label="Saldo pendiente"
              value={formatMoney(patientFocusedMetrics.totalBalance)}
              tone={patientFocusedMetrics.totalBalance > 0 ? "warning" : "neutral"}
            />
            <CompactMetric
              label="Ítems pendientes"
              value={String(patientFocusedMetrics.pendingItems)}
              tone="neutral"
            />
            <CompactMetric
              label="Ítems agendados"
              value={String(patientFocusedMetrics.scheduledItems)}
              tone="neutral"
            />
          </div>
        </>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            borderTop: "1px solid #eef2f7",
            paddingTop: 14
          }}
        >
          <CompactMetric
            label="Activos"
            value={String(summary.active)}
            tone="neutral"
          />
          <CompactMetric
            label="Con saldo"
            value={String(summary.withBalance)}
            tone="warning"
          />
          <CompactMetric
            label="Sin agendar"
            value={String(executiveInsights.plansWithoutAppointment)}
            tone="neutral"
          />
        </div>
      )}
    </div>
  )
}

function CompactMetric({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: {
      bg: "#ffffff",
      border: "#e2e8f0",
      value: "#0f172a"
    },
    warning: {
      bg: "#fffbeb",
      border: "#fde68a",
      value: "#92400e"
    }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        borderRadius: 16,
        padding: 14
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 24,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.05,
          letterSpacing: "-0.03em"
        }}
      >
        {value}
      </div>
    </div>
  )
}